import { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react-lite';
import useUser from 'hooks/use-user';
import { consumeNudgeIntentMarker } from 'src/components/modals/phone-verification-nudge/nudge-intent-marker';
import { trackIntentMarkerConsumed } from 'src/components/modals/phone-verification-nudge/nudge-intent-marker-analytics';
import { usePhoneVerificationNudge } from './use-phone-verification-nudge';

/**
 * Mount-point controller for the OAuth round-trip leg of the Nudge trigger (the direct,
 * same-page login success path is wired straight from `login-content.tsx` instead -- see
 * `usePhoneVerificationNudge`). Rendered once from `global-controllers.jsx`, alongside the rest
 * of the app's mount-point hooks.
 *
 * `hasConsumedRef` guards `consumeNudgeIntentMarker()` (and therefore the trigger call it gates)
 * to fire at most once per login, but only once `User.isLoggedIn` is actually true -- see below.
 * It is re-armed on every logout (`wasLoggedInRef` below), so a second logout -> login cycle in
 * the same tab (no full page reload) can still consume a fresh marker and fire again -- without
 * that reset, `hasConsumedRef` would behave as "once per page load, ever" instead of "once per
 * login," silently dropping the nudge on every login after the first in a given tab.
 *
 * `GlobalControllers` (this hook's mount point) is *not* an `observer`, so a plain
 * `useEffect([User.isLoggedIn])` dependency would only ever see the value from the render that
 * mounted it: `User.isLoggedIn` is a mobx `@computed` hydrated asynchronously by
 * `use-user-controller.tsx`'s effect, so on an SSO landing the marker would be read (and
 * deleted) while still logged out, before the token ever loads -- and the guard above would
 * then block the retrigger once `isLoggedIn` really did flip true. We use the same
 * `useObserver(() => user.isLoggedIn)` pattern as `useCartGuestCustomer` in
 * `use-cart-controller.js` (also mounted, unwrapped, from `GlobalControllers`): `useObserver`
 * subscribes to the mobx reaction itself and force-updates this hook's own render independent of
 * whether any ancestor component is an `observer`, so the effect's dependency array genuinely
 * changes -- and therefore genuinely re-runs -- when the store flips.
 *
 * We deliberately do NOT consume the marker while logged out. Instead we leave it in place:
 * - If the session hydrates (SSO landing), `isLoggedIn` flips to `true`, this effect re-runs,
 *   and we consume + trigger exactly once via `hasConsumedRef`.
 * - If the user abandoned the OAuth flow (bailed at the Google consent screen, navigated back
 *   manually, never actually logged in), the marker is simply never consumed here. It cannot
 *   fire later either: `usePhoneVerificationNudge` itself requires `User.isLoggedIn` before
 *   doing anything, and the marker self-expires via its 5-minute TTL (`consumeNudgeIntentMarker`
 *   already rejects + deletes a stale marker on the next read). So "logged out" + "TTL-bounded"
 *   together make the abandon-and-return case impossible to satisfy, without us ever needing to
 *   eagerly delete the marker on a logged-out read.
 */
export function usePhoneVerificationNudgeController(): void {
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const { triggerPhoneVerificationNudge } = usePhoneVerificationNudge();
  const hasConsumedRef = useRef(false);
  const wasLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    // Re-arm the guard on logout -- see the module docstring above.
    if (wasLoggedInRef.current && !isLoggedIn) {
      hasConsumedRef.current = false;
    }
    wasLoggedInRef.current = isLoggedIn;

    if (hasConsumedRef.current) {
      return;
    }

    if (!isLoggedIn) {
      // Leave the marker untouched -- see the module docstring above.
      return;
    }

    hasConsumedRef.current = true;

    if (!consumeNudgeIntentMarker()) {
      return;
    }

    trackIntentMarkerConsumed();
    void triggerPhoneVerificationNudge({ viaMarker: true });
  }, [isLoggedIn, triggerPhoneVerificationNudge]);
}
